<script setup lang="ts">
import { computed } from "vue";

/**
 * Circular player avatar for match views.
 * Priority: uploaded profile image > Sherlock difficulty artwork > initial letter.
 */
const props = withDefaults(
  defineProps<{
    name: string;
    avatarUrl?: string | null;
    sherlock?: "easy" | "medium" | "hard" | null;
    size?: number;
  }>(),
  {
    avatarUrl: null,
    sherlock: null,
    size: 28,
  },
);

const initial = computed(() => (props.name?.trim()?.[0] ?? "?").toUpperCase());

// Deterministic per-name hue so the same player always gets the same fallback color.
const fallbackHue = computed(() => {
  let h = 0;
  for (const ch of props.name ?? "") h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
});
</script>

<template>
  <span
    class="player-avatar"
    :style="{ width: `${size}px`, height: `${size}px` }"
    :title="name"
  >
    <img v-if="avatarUrl" :src="avatarUrl" :alt="name" />

    <!-- Sherlock (easy): the cheerful apprentice — green, flat cap, big smile -->
    <svg v-else-if="sherlock === 'easy'" viewBox="0 0 64 64" :aria-label="name" role="img">
      <circle cx="32" cy="32" r="32" fill="#5ea862" />
      <path d="M12 64c2-12 10-17 20-17s18 5 20 17z" fill="#3d7a44" />
      <circle cx="32" cy="31" r="14" fill="#f2c9a0" />
      <path
        d="M17.5 27c1.5-10 7.5-14 14.5-14s13 4 14.5 14c-5-3.2-9.5-4.4-14.5-4.4s-9.5 1.2-14.5 4.4z"
        fill="#8a5a33"
      />
      <path d="M14.5 27.5l5-3.5 2.5 4.5z" fill="#8a5a33" />
      <circle cx="26.5" cy="31" r="2.2" fill="#2b2b2b" />
      <circle cx="37.5" cy="31" r="2.2" fill="#2b2b2b" />
      <path
        d="M26 37q6 5.5 12 0"
        stroke="#2b2b2b"
        stroke-width="2"
        fill="none"
        stroke-linecap="round"
      />
      <circle cx="47" cy="46" r="5.5" fill="none" stroke="#ece5d2" stroke-width="2.5" />
      <line x1="51" y1="50" x2="55.5" y2="54.5" stroke="#ece5d2" stroke-width="3" stroke-linecap="round" />
    </svg>

    <!-- Sherlock (medium): the focused investigator — amber, deerstalker, raised brow -->
    <svg v-else-if="sherlock === 'medium'" viewBox="0 0 64 64" :aria-label="name" role="img">
      <circle cx="32" cy="32" r="32" fill="#d3902f" />
      <path d="M12 64c2-12 10-17 20-17s18 5 20 17z" fill="#8a5a1d" />
      <circle cx="32" cy="31" r="14" fill="#f2c9a0" />
      <path
        d="M16.5 26.5c2-10.5 8.5-14.5 15.5-14.5s13.5 4 15.5 14.5l-4.5 1.5c-3.2-2.4-6.8-3.6-11-3.6s-7.8 1.2-11 3.6z"
        fill="#7a5230"
      />
      <path d="M12.5 27.5l6-4 3 5z" fill="#7a5230" />
      <path d="M51.5 27.5l-6-4-3 5z" fill="#7a5230" />
      <rect x="28.5" y="8.5" width="7" height="3.5" rx="1.75" fill="#5d3d22" />
      <path d="M23.5 27.5l6-1.5" stroke="#5a3d22" stroke-width="2" stroke-linecap="round" fill="none" />
      <path d="M40.5 26.5l-6 .8" stroke="#5a3d22" stroke-width="2" stroke-linecap="round" fill="none" />
      <circle cx="26.5" cy="31.5" r="2" fill="#2b2b2b" />
      <circle cx="37.5" cy="31.5" r="2" fill="#2b2b2b" />
      <path
        d="M27.5 38q4.5 3 9 0"
        stroke="#2b2b2b"
        stroke-width="2"
        fill="none"
        stroke-linecap="round"
      />
      <circle cx="47.5" cy="46" r="5.5" fill="none" stroke="#f3ead2" stroke-width="2.5" />
      <line x1="51.5" y1="50" x2="56" y2="54.5" stroke="#f3ead2" stroke-width="3" stroke-linecap="round" />
    </svg>

    <!-- Sherlock (hard): the master detective — crimson, monocle, pipe, stern brows -->
    <svg v-else-if="sherlock === 'hard'" viewBox="0 0 64 64" :aria-label="name" role="img">
      <circle cx="32" cy="32" r="32" fill="#79242f" />
      <path d="M12 64c2-12 10-17 20-17s18 5 20 17z" fill="#43141c" />
      <circle cx="32" cy="31" r="14" fill="#ecbf97" />
      <path
        d="M16.5 26.5c2-10.5 8.5-14.5 15.5-14.5s13.5 4 15.5 14.5l-4.5 1.5c-3.2-2.4-6.8-3.6-11-3.6s-7.8 1.2-11 3.6z"
        fill="#3a3129"
      />
      <path d="M12.5 27.5l6-4 3 5z" fill="#3a3129" />
      <path d="M51.5 27.5l-6-4-3 5z" fill="#3a3129" />
      <rect x="28.5" y="8.5" width="7" height="3.5" rx="1.75" fill="#241e19" />
      <path d="M23 28.5l6.5-2.5" stroke="#241e19" stroke-width="2.2" stroke-linecap="round" fill="none" />
      <path d="M41 28.5l-6.5-2.5" stroke="#241e19" stroke-width="2.2" stroke-linecap="round" fill="none" />
      <circle cx="26.5" cy="32" r="2" fill="#211d1a" />
      <circle cx="37.5" cy="32" r="2" fill="#211d1a" />
      <circle cx="37.5" cy="32" r="4.6" fill="none" stroke="#d9b45b" stroke-width="1.6" />
      <path d="M41 35.5q2 4 1 7" stroke="#d9b45b" stroke-width="1.2" fill="none" />
      <path d="M27 38.5h7" stroke="#211d1a" stroke-width="2" stroke-linecap="round" />
      <path d="M34 38.5q5 1.5 7 4" stroke="#5a4632" stroke-width="2.6" fill="none" stroke-linecap="round" />
      <ellipse cx="43" cy="43.5" rx="3" ry="2.2" fill="#5a4632" />
      <path
        d="M43 39.5q-.8-1.5 0-3"
        stroke="#9a9a9a"
        stroke-width="1.3"
        fill="none"
        stroke-linecap="round"
      />
    </svg>

    <span
      v-else
      class="initial"
      :style="{
        background: `hsl(${fallbackHue}, 45%, 36%)`,
        fontSize: `${Math.round(size * 0.52)}px`,
      }"
    >{{ initial }}</span>
  </span>
</template>

<style scoped>
.player-avatar {
  display: inline-flex;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  vertical-align: middle;
  box-shadow: 0 0 0 1.5px rgba(255, 255, 255, 0.35);
  background: #2a2a2a;
}

.player-avatar img,
.player-avatar svg {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.initial {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  line-height: 1;
  user-select: none;
}
</style>
